/**
 * Copyright (c) 2022 FMR LLC.
 * All Rights Reserved.
 * Fidelity Confidential Information.
 */

import { CDDetail, FractionalCDModel, FractionalCDOfferingsModel } from './../models/fractional-cd-model';
import { ClientConstants, Duration } from './../client.constants';
import { ServerResponse, ServerResponseCD } from './../models/server-response';

import axios from 'axios';

const config = {
    headers: {
        'Content-Type': 'application/json;charset=UTF-8'
    }
};

export default class OfferingsService {
    public static async getOfferings(): Promise<FractionalCDOfferingsModel> {
        try {
            const response = await axios.get(ClientConstants.OFFERINGS_URL, config);
            const mappedResponse = this.mapResponse(response.data);
            return mappedResponse;
        } catch (error) {
            throw new Error(ClientConstants.OFFERINGS_SERVICE_REQUEST_ERROR + '\n' + error);
        }
    }

    private static mapResponse(serverResponse: ServerResponse): FractionalCDOfferingsModel {
        const offerings: FractionalCDModel[] = [];
        offerings.push(this.mapOffering('3mo', Duration.TERM_3MO, serverResponse));
        offerings.push(this.mapOffering('6mo', Duration.TERM_6MO, serverResponse));
        offerings.push(this.mapOffering('9mo', Duration.TERM_9MO, serverResponse));
        offerings.push(this.mapOffering('1yr', Duration.TERM_1YR, serverResponse));
        offerings.push(this.mapOffering('18mo', Duration.TERM_18MO, serverResponse));
        offerings.push(this.mapOffering('2yr', Duration.TERM_2YR, serverResponse));
        return new FractionalCDOfferingsModel(serverResponse.timestamp, offerings);
    }

    private static mapOffering(
        serverDuration: string,
        duration: Duration,
        serverResponse: ServerResponse
    ): FractionalCDModel {
        const offering = serverResponse.topCDOfferings.find(off => off.termToMaturity === serverDuration);
        const minMaturityDate = offering?.minMaturityDate;
        const maxMaturityDate = offering?.maxMaturityDate;
        const fractionalCD = this.buildCDDetail(offering?.fractional);
        const wholeCD = this.buildCDDetail(offering?.whole);
        const sliderRate = fractionalCD === undefined ? '--' : Number(fractionalCD.rate).toFixed(2) + '% APY';
        return new FractionalCDModel(duration, sliderRate, minMaturityDate, maxMaturityDate, fractionalCD, wholeCD);
    }

    private static buildCDDetail(cd?: ServerResponseCD): CDDetail | undefined {
        if (
            cd?.cusip === undefined ||
            cd?.cusip.trim() === '' ||
            cd?.rate === undefined ||
            isNaN(Number(cd?.rate)) ||
            cd?.datedDate === undefined ||
            isNaN(Date.parse(cd?.datedDate)) ||
            cd?.maturityDate === undefined ||
            isNaN(Date.parse(cd?.maturityDate))
        ) {
            return undefined;
        }
        return new CDDetail(cd.cusip, Number(cd.rate), cd.datedDate, cd.maturityDate);
    }
}
